import { useEffect, useState, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { axiosInt } from '../../utils/axios';
import { AuthContext } from './context';
import { TOKEN_LOCAL_STORAGE_KEY } from '../utils/constants';
import { LocalStorage } from '../utils/storage';
import { setTokenStorage } from '../utils/tokenStorage';
import { onTokenRefreshed, setRefreshConfig } from '../utils/refreshToken';
import type { IRefreshConfig } from '../utils/refreshToken';
import {
  getStoredOrgIdForUser,
  setStoredOrgIdForUser,
} from '../utils/resolveOrgFromToken';
import type { IToken, IUserProfile } from '../utils/types';
import { fetchUserProfile, FETCH_USER_PROFILE_KEY } from '../utils/api';
import { Loader } from '../..';
import { useFetchModelInstancesCursorPagination } from '../../ui-builder/hooks/api';

export interface IAuthProviderProps {
  children: ReactNode | ReactNode[];
  models: {
    person: string;
    organization: string;
  };
  oauth2Config: IRefreshConfig;
}

/**
 * Authentication Provider Component
 *
 * Manages authentication state and provides authentication methods throughout the app.
 * Handles token storage, token expiry checking, and logout functionality.
 *
 * Features:
 * - Secure token storage in localStorage
 * - Automatic token expiry checking
 * - Silent token refresh capability
 * - Centralized logout functionality
 */
const AuthProvider: React.FC<IAuthProviderProps> = ({
  children,
  models,
  oauth2Config,
}) => {
  // Registered during render rather than in an effect: children may fire a request - and so need
  // a working refresh - before an effect on this provider would have run. Assigning the config is
  // idempotent, so repeating it on re-render is harmless.
  setRefreshConfig(oauth2Config);

  const storedToken = LocalStorage.get(TOKEN_LOCAL_STORAGE_KEY);

  const [token, setToken] = useState<IToken | undefined>(
    storedToken ? storedToken : undefined
  );

  const { data: user, isLoading: isFetchingUser } = useQuery<IUserProfile>({
    queryKey: [FETCH_USER_PROFILE_KEY],
    queryFn: () => fetchUserProfile(axiosInt),
    enabled: !!token && !!axiosInt,
  });

  const {
    modelInstances: persons,
    isFirstLoadingModelInstaces: isLoadingPersons,
  } = useFetchModelInstancesCursorPagination({
    params: {
      page: 1,
      size: 1,
      modelIds: [models.person],
      filters: JSON.stringify({
        '==': [
          {
            var: 'ims.id',
          },
          user?.id,
        ],
      }),
    },
    options: {
      enabled: !!user,
    },
  });

  const {
    modelInstances: organizations,
    isFirstLoadingModelInstaces: isLoadingOrganizations,
  } = useFetchModelInstancesCursorPagination({
    params: {
      page: 1,
      size: 1,
      modelIds: [models.organization],
      filters: JSON.stringify({
        '==': [
          {
            var: 'ims.id',
          },
          user?.userOrgRoles?.[0]?.orgId,
        ],
      }),
    },
    options: {
      enabled: !!user && user.userOrgRoles?.length > 0,
    },
  });

  const actor = useMemo(() => {
    return {
      ...persons?.data?.[0],
      organization: organizations?.data?.[0],
    };
  }, [persons, organizations]);

  // The interceptor refreshes the token outside React, so mirror it into state - otherwise this
  // keeps holding the expired one for the rest of the session.
  useEffect(() => onTokenRefreshed(setToken), []);

  useEffect(() => {
    if (user && user.userOrgRoles?.length > 0) {
      const firstOrgId = user.userOrgRoles[0].orgId;

      const existingOrgId = getStoredOrgIdForUser(user.id);

      if (!existingOrgId || existingOrgId !== firstOrgId) {
        setStoredOrgIdForUser(user.id, firstOrgId);
      }
    }
  }, [user]);

  /**
   * Sets user token and updates axios headers for API requests.
   * Called after successful authentication or token refresh.
   *
   * @param token - Token object containing access token
   */
  const setUserToken = (token: IToken) => {
    setToken(token);
    // Store the whole token: dropping refreshToken here would leave nothing to refresh with.
    setTokenStorage(token);
  };

  /**
   * Performs logout by clearing tokens and resetting application state.
   * Clears localStorage, resets axios headers, and updates component state.
   */
  const logout = () => {
    setToken(undefined);
    LocalStorage.remove(TOKEN_LOCAL_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        actor: actor,
        isAuthInitializing:
          isFetchingUser || isLoadingPersons || isLoadingOrganizations,
        setUserToken,
        logout,
      }}
    >
      {isFetchingUser || isLoadingPersons || isLoadingOrganizations ? (
        <Loader />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
